import { useState, useEffect } from 'react'
import { getServerSessionId } from '../../lib/serverSession'

interface Phase {
  id: number
  phase_number: number
  phase_name: string
  price_per_share: string
  total_shares_available: string
  shares_sold: string
  is_active: boolean
  commission_structure: string
  start_date: string | null
  end_date: string | null
}

export default function PhaseTransitionManager() {
  const [phases, setPhases] = useState<Phase[]>([])
  const [currentPhase, setCurrentPhase] = useState<Phase | null>(null)
  const [loading, setLoading] = useState(true)
  const [transitioning, setTransitioning] = useState(false)
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPhases().catch(err => {
      console.error('Failed to load phases:', err)
      setError(err.message || 'Failed to load phases')
      setLoading(false)
    })
  }, [])

  const loadPhases = async () => {
    try {
      setLoading(true)
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/phase-transition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'load_phases' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      setPhases(result.data?.phases || [])
      const active = result.data?.phases?.find((p: Phase) => p.is_active)
      setCurrentPhase(active || null)
    } catch (error) {
      console.error('Error loading phases:', error)
      setMessage({ type: 'error', text: 'Failed to load phases' })
    } finally {
      setLoading(false)
    }
  }

  const transitionToPhase = async (targetPhaseNumber: number) => {
    if (!confirm(`Are you sure you want to transition to Phase ${targetPhaseNumber}?\n\nThis will:\n1. End the current phase\n2. Move unsold shares to the new phase\n3. Activate the new commission structure\n4. Update all pending transactions to use the new structure\n\nThis action cannot be undone!`)) {
      return
    }

    try {
      setTransitioning(true)
      setMessage(null)

      if (!currentPhase) {
        throw new Error('No current phase active')
      }

      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/phase-transition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({
          operation: 'transition_to_phase',
          data: { targetPhaseNumber, currentPhaseId: currentPhase.id }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      console.log(`✅ Phase transition complete!`)
      console.log(`   Unsold shares moved: ${result.data?.unsoldSharesMoved}`)
      console.log(`   New total available: ${result.data?.newTotalAvailable}`)
      console.log(`   Commission structure: ${result.data?.commissionStructure}`)

      setMessage({
        type: 'success',
        text: `${result.data?.message}! ${result.data?.unsoldSharesMoved?.toLocaleString()} unsold shares moved. New commission structure: ${result.data?.commissionStructure}`
      })

      // Reload phases
      await loadPhases()

    } catch (error) {
      console.error('Phase transition error:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Phase transition failed'
      })
    } finally {
      setTransitioning(false)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: '#ffffff' }}>Loading phases...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          backgroundColor: '#7f1d1d',
          border: '1px solid #991b1b',
          borderRadius: '8px',
          padding: '16px',
          color: '#fecaca'
        }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid #374151'
    }}>
      <h2 style={{
        color: '#f59e0b',
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        🔄 Phase Transition Manager
      </h2>

      {message && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
          color: message.type === 'success' ? '#10b981' : '#ef4444'
        }}>
          {message.text}
        </div>
      )}

      {/* Current Phase Info */}
      {currentPhase && (
        <div style={{
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#3b82f6', fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
            📍 Current Active Phase
          </h3>
          <div style={{ color: '#e5e7eb', fontSize: '14px', lineHeight: '1.6' }}>
            <div><strong>Phase:</strong> {currentPhase.phase_name} (Phase {currentPhase.phase_number})</div>
            <div><strong>Price per Share:</strong> ${currentPhase.price_per_share}</div>
            <div><strong>Shares Sold:</strong> {parseFloat(currentPhase.shares_sold).toLocaleString()} / {parseFloat(currentPhase.total_shares_available).toLocaleString()}</div>
            <div><strong>Unsold Shares:</strong> {(parseFloat(currentPhase.total_shares_available) - parseFloat(currentPhase.shares_sold)).toLocaleString()}</div>
            <div><strong>Commission Structure:</strong> <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>{currentPhase.commission_structure}</span></div>
            {currentPhase.commission_structure === 'presale_15_15' && (
              <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                └ 15% USDT + 15% Bonus Shares (Single Level)
              </div>
            )}
            {currentPhase.commission_structure === 'multilevel_10_5' && (
              <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                └ 10% USDT (L1) + 5% Cascading Share Purchases (Unlimited Levels)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase List */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ color: '#e5e7eb', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
          All Phases
        </h3>
        <div style={{ display: 'grid', gap: '12px' }}>
          {phases.map(phase => (
            <div
              key={phase.id}
              style={{
                backgroundColor: phase.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(55, 65, 81, 0.5)',
                border: phase.is_active ? '2px solid #10b981' : '1px solid #374151',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  color: phase.is_active ? '#10b981' : '#e5e7eb',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '4px'
                }}>
                  {phase.phase_name} {phase.is_active && '(ACTIVE)'}
                </div>
                <div style={{ color: '#ffffff', fontSize: '13px' }}>
                  ${phase.price_per_share}/share • {parseFloat(phase.shares_sold).toLocaleString()} / {parseFloat(phase.total_shares_available).toLocaleString()} sold
                </div>
                <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                  Commission: {phase.commission_structure}
                </div>
              </div>
              
              {!phase.is_active && currentPhase && phase.phase_number > currentPhase.phase_number && (
                <button
                  onClick={() => transitionToPhase(phase.phase_number)}
                  disabled={transitioning}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: transitioning ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    opacity: transitioning ? 0.5 : 1
                  }}
                >
                  {transitioning ? 'Transitioning...' : 'Activate'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Warning */}
      <div style={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid #ef4444',
        borderRadius: '8px',
        padding: '12px',
        color: '#ef4444',
        fontSize: '13px'
      }}>
        ⚠️ <strong>Warning:</strong> Phase transitions are permanent and will immediately affect all new purchases. Ensure you're ready before activating a new phase.
      </div>
    </div>
  )
}

